import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Platform } from 'react-native';

import { PHONE_NAV_ITEMS } from './v2-nav-groups';
import V2Sidebar from './v2-sidebar';

import { useAccountingPermissions, useWorkOrderBillingQueue } from '@/hooks/use-accounting';
import { useApi } from '@/hooks/use-api';
import { useWorkOrderQueue } from '@/hooks/use-approval-queue';
import { useApplicationCounts, useLabels, useTasks, useWorkOrders } from '@/hooks/use-data';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useFitsReports } from '@/hooks/use-fits-reports';
import { useIsNativePhone } from '@/hooks/use-is-native-phone';
import { usePendingOwnershipTransferCount } from '@/hooks/use-ownership-transfers';
import { useAdminOrFlagged, usePermissions } from '@/hooks/use-permissions';
import { useReviewRequestCounts } from '@/hooks/use-review-requests';
import { organizationService } from '@/services/organization-service';
import { FeatureFlagName } from '@/types/models';
import { canAccessIntakeQueues } from '@/utils/intake-queue-access';
import { applyTasksFilter } from '@/utils/tasks-view';
import { myWorkOrdersLabel } from '@/utils/work-order-views';

interface CustomDrawerContentProps {
	navigation: {
		closeDrawer: () => void;
	};
	/** Icons-only collapsed rail (desktop). */
	collapsed?: boolean;
	/** Toggle collapse — only provided on large screens where collapse is offered. */
	onToggleCollapsed?: () => void;
}

/**
 * The organization record, for the sidebar brand row's logo. Same query key as Settings →
 * Organization and the dashboard, so this is served from cache rather than a new request.
 */
const useOrganization = () => {
	const { user } = useApi();
	return useQuery({
		queryKey: ['organization'],
		queryFn: () => organizationService.get(),
		enabled: !!user?.id,
	});
};

/**
 * The iPhone app's drawer (#684): Work Orders and Settings, for every role.
 *
 * Its own component rather than a branch inside the full one, so a phone runs none of the
 * badge-count queries below for rows it never shows.
 */
const PhoneDrawerContent: React.FC<CustomDrawerContentProps> = ({ navigation }) => {
	const { data: organization } = useOrganization();
	return (
		<V2Sidebar
			navigation={navigation}
			menu={PHONE_NAV_ITEMS}
			brand={{ logo: organization?.logo, name: organization?.name }}
		/>
	);
};

/**
 * The full drawer: resolves every live count and permission gate, then hands them to
 * `V2Sidebar` as plain data, keeping `v2-nav-groups.ts` free of permission logic.
 */
const FullDrawerContent: React.FC<CustomDrawerContentProps> = ({ navigation, collapsed = false, onToggleCollapsed }) => {
	const permissions = usePermissions();
	const { user } = useApi();
	const { flags, hasFlag } = useFeatureFlags();
	const { allowed: canEditTemplates } = useAdminOrFlagged(FeatureFlagName.EmailTemplatesAdmin);
	const fitsReports = useFitsReports();
	// Row and fetch share one predicate: these three rows are gated to maintenance staff, so
	// nobody else pays for a count they cannot see.
	const isMaintenanceStaff = permissions.isMaintenance || permissions.isContractor;
	const { data: pendingWorkOrders } = useWorkOrders({
		status: 'pending_materials,pending_vendor', limit: 1, enabled: isMaintenanceStaff,
	});
	const pendingCount = pendingWorkOrders?.total ?? 0;
	const { data: unassignedWorkOrders } = useWorkOrders({
		assignedTo: 'unassigned', status: 'open,in_progress', limit: 1, enabled: permissions.isMaintenance,
	});
	const unassignedCount = unassignedWorkOrders?.total ?? 0;
	const { data: delegatedWorkOrders } = useWorkOrders({
		assignedTo: 'others', status: 'open,in_progress', limit: 1, enabled: permissions.isMaintenance,
	});
	const delegatedCount = delegatedWorkOrders?.total ?? 0;
	/**
	 * The two review-chain badges (#496, split into two rows).
	 *
	 * Each reads the SAME hook its page and its dashboard tile do, so a badge cannot show a
	 * different number from the screen it links to. Disabled — no fetch at all — for a
	 * viewer whose gate hides the row; the query key is shared with the page and the tile,
	 * so TanStack serves all three from one request when it does run.
	 */
	const reviewQueue = useWorkOrderQueue('needs_review', { enabled: permissions.canViewReviewQueue });
	const approvalQueue = useWorkOrderQueue('pending_approval', { enabled: permissions.canViewApprovalQueue });
	/**
	 * Needs Billing — the third stage of the same chain. Gated to reviewers who ALSO hold the
	 * accounting flag: the billing API answers 404 without it, so the row and the fetch share
	 * one predicate. `limit: 1` because only `total` is read; the page fetches its own rows.
	 */
	const canViewBillingQueue = permissions.canViewReviewQueue && hasFlag('accounting');
	const { data: billingQueue } = useWorkOrderBillingQueue({ limit: 1 }, canViewBillingQueue);
	const needsBillingCount = billingQueue?.total ?? 0;
	/**
	 * Open follow-up tasks, ANY assignee — the same set the Follow Up Tasks page lists, so
	 * the badge and the page agree. (The To Do panel's follow-ups are the viewer's own; that
	 * is a different, narrower list, and a badge that used it read 1 over a page showing 2.)
	 * Only fetched for viewers who can see the row.
	 */
	const { data: openTasks } = useTasks({ status: 'open,in_progress', limit: 500, enabled: hasFlag('tasks_enabled') });
	const followUpCount = useMemo(
		() => applyTasksFilter(openTasks?.data ?? [], 'follow-ups').length,
		[openTasks],
	);
	/**
	 * My Work Orders / Overdue counts. `limit: 1` because only `total` is read — the same
	 * shape as the pending/unassigned/delegated counts above. Both filters are resolved
	 * server-side, so these are exactly the totals their pages show.
	 */
	const { data: myWorkOrders } = useWorkOrders({ scope: 'mine', status: 'open,in_progress', limit: 1 });
	const myWorkOrdersCount = myWorkOrders?.total ?? 0;
	const { data: overdueWorkOrders } = useWorkOrders({ overdue: true, status: 'open,in_progress', limit: 1 });
	const overdueCount = overdueWorkOrders?.total ?? 0;
	const { data: organization } = useOrganization();
	// Unfiled-only, from `useLabels`' default — see the rule there. These are open
	// indicators, so they count what the lists they open count, and they share the rail's
	// cache entry rather than issuing a second request for a number that has to match.
	const { data: labelRegistry } = useLabels({ enabled: permissions.canUseInbox });
	const { data: applicationCounts } = useApplicationCounts({ enabled: permissions.canUseInbox });
	/**
	 * `needsHuman` (= pendingUs + unconverted), NOT `pendingUs`.
	 *
	 * The row opens Needs Review, whose header totals BOTH sections — unmatched submissions
	 * and requests pending our review. Badging only `pendingUs` meant the sidebar said 11 and
	 * the screen it opened said "15 items waiting on this team".
	 */
	const pendingApplications = applicationCounts?.needsHuman ?? 0;
	/**
	 * Resale packets still sitting in someone's queue — `status = 'new'`, whatever the
	 * matcher concluded, which is exactly the set the page's default chip opens.
	 *
	 * Counts unmatched rows ON PURPOSE. A packet whose address matched nothing is the one
	 * most likely to be forgotten, and excluding it would make the badge go quiet precisely
	 * when someone needs to intervene.
	 *
	 * Rows and fetches share ONE predicate, `canAccessIntakeQueues` — the same function the
	 * API gates every `/accounting/ownership-transfers` and `/accounting/review-requests` route
	 * on (Admin role, Accounting role, or `accounting_admin`, each with the accounting flag). A
	 * property manager holding only the accounting flag gets neither row nor a badge request
	 * that would 404.
	 */
	const canViewIntakeQueues = canAccessIntakeQueues({ role: user?.role, flags });
	const { count: pendingTransfers } = usePendingOwnershipTransferCount({ enabled: canViewIntakeQueues });
	/**
	 * Needs Review's badge — same predicate, same reason: review requests carry the agent's
	 * escalations about transfer packets.
	 *
	 * `badge` is pending PLUS failed. A failed request is one a person already approved and
	 * whose handler then threw; it is settled by status, appears in no pending view, and is
	 * mentioned on no other screen — so leaving it out of the badge is how an authorized
	 * action that silently failed stays invisible.
	 */
	const { badge: pendingReviews } = useReviewRequestCounts({ enabled: canViewIntakeQueues });
	/*
	 * "All mail" carries NO badge, and the request that fed it is gone with it.
	 *
	 * It counted the whole working inbox. For a property manager that includes every message
	 * already on a colleague's desk — 30 bill-pay sign-ups routed to the bookkeeper read as
	 * 30 things she owed — so the number was unclearable by the person reading it. All mail
	 * is a place you go to look something up; the two rows below it are the ones that mean
	 * work. (It counted "Needs a Human" before that, which was right when the row was called
	 * Inbox and is a different set again.)
	 *
	 * The `/messages?limit=1&unfiled=1` fetch existed ONLY for this badge, so removing it
	 * takes a request off every page load for everyone with inbox access.
	 */
	/**
	 * Mail handed to this person and still open — the red drawer badge.
	 *
	 * Reuses the count the rail already renders rather than adding a request: it comes off
	 * the same `/messages/labels` call, filtered server-side to `assigned_to = me AND
	 * status = 'open'`, so the drawer and the rail cannot report different numbers.
	 */
	const assignedToMeCount = labelRegistry?.assignedToMe ?? 0;
	/**
	 * Mail nobody has filed to an owner yet — the Needs Triage row.
	 *
	 * NOT unread: reading a message does not file it, so an unread-based count would drop
	 * items out of the queue the moment somebody glanced at them. This is the same DISTINCT
	 * set the rail's own triage section and `?label=needs-human` show, computed once
	 * server-side so the three cannot disagree.
	 */
	const needsTriageCount = labelRegistry?.needsHuman ?? 0;
	/**
	 * Vendors + the org's own vendor record: accounting staff only (#578). `canModify` is the
	 * client's mirror of CREATE_VENDORS — Admin, Accounting, or `accounting_admin` — which is
	 * what excludes a property manager; VIEW_VENDORS would not, every role holds it.
	 */
	const { canModify: canWriteAccounting } = useAccountingPermissions();
	// A soft-deleted self vendor keeps its pointer but 404s — offer no row for it, the way
	// billing treats the org as unconfigured.
	const selfVendorId = organization?.selfVendorActive ? organization.selfVendorId : undefined;
	const ids = useMemo(() => ({ selfVendor: selfVendorId }), [selfVendorId]);

	return (
		<V2Sidebar
			navigation={navigation}
			collapsed={collapsed}
			onToggleCollapsed={onToggleCollapsed}
			gates={{
				inspections: permissions.canViewInspections,
				maintenance: permissions.isMaintenance,
				// Both do the work, and both chase a blocked job.
				maintenanceStaff: isMaintenanceStaff,
				tasks: hasFlag('tasks_enabled'),
				inbox: permissions.canUseInbox,
				users: permissions.canViewUsers,
				materials: permissions.canViewMaterials,
				// Same rule as Home's Quick Links tile — see `useFitsReports`.
				reports: permissions.canViewReports && fitsReports,
				vendors: hasFlag('accounting') && canWriteAccounting,
				accountingAdmin: hasFlag('accounting') && canWriteAccounting,
				// The SECTION is web-only and nothing more; each row inside decides who
				// sees it. V2Sidebar drops a group with no visible rows, so a user with
				// no admin capability at all still never sees the header.
				adminSection: Platform.OS === 'web',
				superAdmin: permissions.canAccessAdmin && Platform.OS === 'web',
				// The read-across-the-org tools. A core capability holder needs the audit
				// log and the location history to do the job they were given.
				adminTools: permissions.hasAdminCapability && Platform.OS === 'web',
				// Import Batches is an admin tool over ACCOUNTING data: every endpoint it
				// calls goes through `ensureAccountingFlag`, which 404s the family without
				// the flag. So it needs both, or the row opens an empty, erroring page. Accounting
				// WRITERS qualify too: its actions (re-project, NSF repair, routine revert) are
				// accounting writes the server grants them — same condition as the screen guard.
				adminAccountingTools: (permissions.hasAdminCapability || canWriteAccounting)
					&& hasFlag('accounting') && Platform.OS === 'web',
				// The flag needs the ROLE as a floor: api/index.ts gates /admin/sql on
				// `role === Admin`, so a flag-only holder would get a row whose every
				// query 403s — the same trap the inboxRules note below describes.
				sqlAdmin: hasFlag('sql_admin') && permissions.canAccessAdmin,
				settings: permissions.canViewSettings,
				reviewQueue: permissions.canViewReviewQueue,
				approvalQueue: permissions.canViewApprovalQueue,
				billingQueue: canViewBillingQueue,
				// The Admin role or `association_admin`, matching what api/message-rules.ts
				// enforces. A row that opens a page whose every action 403s is worse than no row.
				inboxRules: permissions.isAssociationAdmin && permissions.canUseInbox,
				/**
				 * Matches `canEditTemplates` in app/settings/email-templates/index.tsx exactly —
				 * the same `useAdminOrFlagged` answer. Two answers to "may this person edit the
				 * wording?" that could drift is how you get a row leading to a read-only page.
				 */
				emailTemplates: canEditTemplates && permissions.canUseInbox,
				intakeQueues: canViewIntakeQueues,
			}}
			labels={{ myWorkOrders: myWorkOrdersLabel(user), selfVendor: organization?.name }}
			ids={ids}
			brand={{ logo: organization?.logo, name: organization?.name }}
			badges={{
				pending: pendingCount,
				needsReview: reviewQueue.count,
				pendingApproval: approvalQueue.count,
				needsBilling: needsBillingCount,
				followUps: followUpCount,
				myWorkOrders: myWorkOrdersCount,
				overdue: overdueCount,
				unassigned: unassignedCount,
				delegated: delegatedCount,
				assignedToMe: assignedToMeCount,
				needsTriage: needsTriageCount,
				applications: pendingApplications,
				ownershipTransfers: pendingTransfers,
				reviewRequests: pendingReviews,
			}}
		/>
	);
};

/** The drawer's CONTENT — the two-row phone menu on the iPhone app, the full sidebar elsewhere. */
const CustomDrawerContent: React.FC<CustomDrawerContentProps> = (props) => {
	const isNativePhone = useIsNativePhone();
	return isNativePhone ? <PhoneDrawerContent {...props} /> : <FullDrawerContent {...props} />;
};

export default CustomDrawerContent;
